import { useConvexAction, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { useMutation } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Loader2, Sparkles } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type GeneratedQuestion = {
  readonly text: string
  readonly competency: string
}

type GenerateTab = 'text' | 'url' | 'pdf'

const TAB_LABEL_KEY = {
  text: 'jobs.generate.tabText',
  url: 'jobs.generate.tabUrl',
  pdf: 'jobs.generate.tabPdf',
} as const

interface GenerateQuestionsPanelProps {
  readonly jobId: Id<'jobs'>
  readonly onAddQuestions: (questions: ReadonlyArray<GeneratedQuestion>) => void
}

export const GenerateQuestionsPanel = ({
  jobId,
  onAddQuestions,
}: GenerateQuestionsPanelProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<GenerateTab>('text')
  const [text, setText] = useState('')
  const [url, setUrl] = useState('')
  const [pdfFile, setPdfFile] = useState<File | undefined>(undefined)
  const [results, setResults] = useState<ReadonlyArray<GeneratedQuestion>>([])
  const [discarded, setDiscarded] = useState<ReadonlySet<number>>(new Set())
  const [error, setError] = useState<string | undefined>(undefined)
  const [isGenerating, setIsGenerating] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { mutateAsync: generateFromText } = useMutation({
    mutationFn: useConvexAction(
      api.features.jobs.generate_questions.generateMyJobQuestionsFromText,
    ),
  })

  const { mutateAsync: generateFromUrl } = useMutation({
    mutationFn: useConvexAction(
      api.features.jobs.generate_questions.parseMyJobDescriptionUrl,
    ),
  })

  const { mutateAsync: generateFromPdf } = useMutation({
    mutationFn: useConvexAction(
      api.features.jobs.generate_questions.parseMyJobDescriptionPdf,
    ),
  })

  const { mutateAsync: getUploadUrl } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.jobs.generateMyPdfUploadUrl,
    ),
  })

  const handleGenerate = async (): Promise<void> => {
    setError(undefined)
    setIsGenerating(true)
    setResults([])
    setDiscarded(new Set())

    try {
      let generated: ReadonlyArray<GeneratedQuestion>

      if (activeTab === 'text') {
        generated = await generateFromText({ jobId, text })
      } else if (activeTab === 'url') {
        generated = await generateFromUrl({ jobId, url })
      } else {
        if (!pdfFile) return
        const uploadUrl = await getUploadUrl({})
        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': pdfFile.type },
          body: pdfFile,
        })
        const { storageId } = (await uploadResponse.json()) as {
          storageId: Id<'_storage'>
        }
        generated = await generateFromPdf({ jobId, storageId })
      }

      setResults(generated)
    } catch {
      setError(t('jobs.generate.error'))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAddOne = (index: number): void => {
    const question = results[index]
    if (!question) return
    onAddQuestions([question])
    setDiscarded((prev) => new Set([...prev, index]))
  }

  const handleDiscardOne = (index: number): void => {
    setDiscarded((prev) => new Set([...prev, index]))
  }

  const handleAddAll = (): void => {
    const remaining = results.filter((_, i) => !discarded.has(i))
    onAddQuestions(remaining)
    setDiscarded(new Set(results.map((_, i) => i)))
  }

  const handleDiscardAll = (): void => {
    setDiscarded(new Set(results.map((_, i) => i)))
  }

  const isGenerateDisabled =
    isGenerating ||
    (activeTab === 'text' && !text.trim()) ||
    (activeTab === 'url' && !url.trim()) ||
    (activeTab === 'pdf' && !pdfFile)

  const remainingCount = results.filter((_, i) => !discarded.has(i)).length

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        className="flex w-full items-center justify-between p-4 text-left"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary/60" />
          <span className="text-sm font-medium text-primary">
            {t('jobs.generate.title')}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-primary/40" />
        ) : (
          <ChevronDown className="h-4 w-4 text-primary/40" />
        )}
      </button>

      {isOpen && (
        <div className="flex flex-col gap-4 border-t border-border p-4">
          <div className="flex gap-1 rounded-md border border-border p-1">
            {(['text', 'url', 'pdf'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-primary text-primary-foreground'
                    : 'text-primary/60 hover:text-primary'
                }`}
              >
                {t(TAB_LABEL_KEY[tab])}
              </button>
            ))}
          </div>

          {activeTab === 'text' && (
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('jobs.generate.textPlaceholder')}
              className="min-h-[120px] resize-none"
            />
          )}

          {activeTab === 'url' && (
            <Input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('jobs.generate.urlPlaceholder')}
            />
          )}

          {activeTab === 'pdf' && (
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => setPdfFile(e.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                {t('jobs.generate.pdfLabel')}
              </Button>
              {pdfFile && (
                <span className="text-sm text-primary/60">{pdfFile.name}</span>
              )}
            </div>
          )}

          <Button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerateDisabled}
            className="self-start"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('jobs.generate.generating')}
              </>
            ) : (
              t('jobs.generate.generate')
            )}
          </Button>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {results.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-primary/50">
                  {remainingCount} / {results.length}
                </span>
                {remainingCount > 0 && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddAll}
                      className="text-xs text-primary/60 hover:text-primary"
                    >
                      {t('jobs.generate.addAll')}
                    </button>
                    <span className="text-primary/20">·</span>
                    <button
                      type="button"
                      onClick={handleDiscardAll}
                      className="text-xs text-destructive/60 hover:text-destructive"
                    >
                      {t('jobs.generate.discardAll')}
                    </button>
                  </div>
                )}
              </div>

              {results.map((q, i) => {
                if (discarded.has(i)) return null
                return (
                  <div
                    key={i}
                    className="flex flex-col gap-2 rounded-md border border-border bg-background p-3"
                  >
                    <p className="text-sm text-primary">{q.text}</p>
                    {q.competency && (
                      <span className="self-start rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary/70">
                        {q.competency}
                      </span>
                    )}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleAddOne(i)}
                        className="text-xs text-primary/60 hover:text-primary"
                      >
                        {t('jobs.generate.addToJob')}
                      </button>
                      <span className="text-primary/20">·</span>
                      <button
                        type="button"
                        onClick={() => handleDiscardOne(i)}
                        className="text-xs text-destructive/60 hover:text-destructive"
                      >
                        {t('jobs.generate.discard')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
